import { createContext, useCallback, useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { setToastHandler, type ToastLike } from '../lib/api';

export interface ToastRecord extends ToastLike {
  id: number;
}

export interface ToastValue {
  toasts: ToastRecord[];
  pushToast: (t: ToastLike) => void;
  dismissToast: (id: number) => void;
}

export const ToastContext = createContext<ToastValue | null>(null);

const MAX_TOASTS = 3;
const DEFAULT_DURATION_MS = 4000;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastRecord[]>([]);
  const idRef = useRef(0);
  const timersRef = useRef<Map<number, ReturnType<typeof setTimeout>>>(new Map());

  const dismissToast = useCallback((id: number) => {
    setToasts(prev => prev.filter(t => t.id !== id));
    const timer = timersRef.current.get(id);
    if (timer) { clearTimeout(timer); timersRef.current.delete(id); }
  }, []);

  const pushToast = useCallback((t: ToastLike) => {
    const id = ++idRef.current;
    const record: ToastRecord = { ...t, id };
    setToasts(prev => {
      const next = [...prev, record];
      // drop oldest past MAX
      return next.length > MAX_TOASTS ? next.slice(next.length - MAX_TOASTS) : next;
    });
    const durationMs = t.durationMs ?? DEFAULT_DURATION_MS;
    const timer = setTimeout(() => dismissToast(id), durationMs);
    timersRef.current.set(id, timer);
  }, [dismissToast]);

  // Register with api.ts so RateLimitedError surfaces through toast
  useEffect(() => {
    setToastHandler(pushToast);
    return () => setToastHandler(null);
  }, [pushToast]);

  // Cleanup timers on unmount
  useEffect(() => {
    const timers = timersRef.current;
    return () => { timers.forEach(clearTimeout); timers.clear(); };
  }, []);

  return (
    <ToastContext.Provider value={{ toasts, pushToast, dismissToast }}>
      {children}
    </ToastContext.Provider>
  );
}
